import { cn } from '@/lib/utils'

export interface SegmentedOption<T extends string> {
  value: T
  /** Full visible label, count included (e.g. `Open (6)`) — Playwright pins it. */
  label: string
  /** Small brand dot before the label (the "open"/live option). */
  withDot?: boolean
}

/**
 * Exclusive filter switch: a bordered paper track of REAL `<button
 * aria-pressed>` segments (the a11y contract the filter pills established —
 * count-in-label, pressed state). Active segment lifts onto the card surface.
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  className,
}: {
  /** Accessible name for the group. */
  label: string
  options: SegmentedOption<T>[]
  value: T
  onChange: (value: T) => void
  className?: string
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn('inline-flex rounded-md border border-rule bg-paper p-0.5', className)}
    >
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-[5px] px-3 py-1.5 font-mono text-xs transition-colors duration-[180ms] ease-editorial',
              active ? 'bg-card text-ink shadow-overlay' : 'text-inkMid hover:text-ink',
            )}
          >
            {option.withDot ? (
              <span
                aria-hidden="true"
                className={cn('size-1.5 rounded-full', active ? 'bg-brand' : 'bg-accentSoft')}
              />
            ) : null}
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
